// Compliance UI, shared by the lab console and the clinic portal.
//
// Both audiences read the same server projection, so they get the same components here too: if the
// lab tells a doctor "she's at 71%", the doctor's own screen must not say something else. Only the
// file URL differs (staff route vs portal route), which is why it is passed in.
import { useEffect, useState, type ReactNode } from 'react';
import {
  complianceLabel,
  formatDate,
  PHOTO_POSE_LABELS,
  wearHours,
  type CaseCompliance,
  type ComplianceDay,
  type ProgressPhoto,
} from '@alignova/shared';
import { IconAlert, IconCheck, IconClose, IconClock } from './icons.tsx';
import { useScrollLock } from './mobile.tsx';

const TONE_CLS = {
  good: 'bg-status-progress-bg text-status-progress-fg',
  warn: 'bg-status-warn-bg text-status-warn-fg',
  bad: 'bg-status-urgent-bg text-status-urgent-fg',
} as const;

/** Adherence badge — the same words on every surface. */
export function ComplianceBadge({ adherence, small = false }: { adherence: number; small?: boolean }) {
  const { label, tone } = complianceLabel(adherence);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${TONE_CLS[tone]} ${
        small ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'
      }`}
    >
      {label}
      <span className="tabnum opacity-70">{adherence}%</span>
    </span>
  );
}

/**
 * The wear history. One bar per day, tallest = the daily goal; bars that met the target take the
 * accent, misses take the warn tone, and days with nothing recorded stay as an empty track so a
 * gap reads as "no data" rather than "zero hours".
 */
export function WearChart({
  days,
  targetHours,
  height = 96,
}: {
  days: ComplianceDay[];
  targetHours: number;
  height?: number;
}) {
  const [hover, setHover] = useState<ComplianceDay | null>(null);
  const max = Math.max(targetHours, ...days.map((d) => d.seconds / 3600));
  return (
    <div>
      <div className="relative flex items-end gap-[2px]" style={{ height }}>
        {/* The goal line: everything below it is a miss. */}
        <div
          className="pointer-events-none absolute inset-x-0 border-t border-dashed border-accent/50"
          style={{ bottom: `${(targetHours / max) * 100}%` }}
        />
        {days.map((d) => {
          const h = (d.seconds / 3600 / max) * 100;
          return (
            <button
              key={d.day}
              type="button"
              onMouseEnter={() => setHover(d)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(d)}
              onBlur={() => setHover(null)}
              onClick={() => setHover(d)}
              title={`${formatDate(d.day)} · ${wearHours(d.seconds)}h`}
              aria-label={`${formatDate(d.day)}: ${wearHours(d.seconds)} hours`}
              className="flex h-full flex-1 items-end rounded-sm bg-surface-2"
            >
              <span
                className={`w-full rounded-sm transition-all ${d.onTarget ? 'bg-accent' : 'bg-status-warn-fg/70'}`}
                style={{ height: `${Math.max(d.seconds > 0 ? 4 : 0, h)}%` }}
              />
            </button>
          );
        })}
      </div>
      <div className="mt-1.5 flex items-baseline justify-between text-[11px] text-muted">
        <span>{days.length ? formatDate(days[0]!.day) : ''}</span>
        <span className="tabnum font-medium text-ink">
          {hover ? `${formatDate(hover.day)} · ${wearHours(hover.seconds)}h` : `Goal ${targetHours}h/day`}
        </span>
        <span>Today</span>
      </div>
    </div>
  );
}

/** The four numbers a reviewer actually quotes. */
export function ComplianceStats({ c }: { c: CaseCompliance }) {
  const stats: Array<{ label: string; value: string; sub?: string }> = [
    { label: 'Avg / day', value: `${c.avgHours7}h`, sub: 'last 7 days' },
    { label: 'On target', value: `${c.adherence7}%`, sub: `${7 - c.daysBelowTarget7} of 7 days` },
    { label: 'Streak', value: `${c.streak}d`, sub: 'to yesterday' },
    { label: '30-day avg', value: `${c.avgHours30}h`, sub: `${c.adherence30}% on target` },
  ];
  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {stats.map((s) => (
        <div key={s.label} className="rounded-md bg-surface-2 px-3 py-2">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">{s.label}</dt>
          <dd className="tabnum text-lg font-semibold text-ink">{s.value}</dd>
          {s.sub && <dd className="text-[11px] text-muted">{s.sub}</dd>}
        </div>
      ))}
    </dl>
  );
}

/** "Wearing now" / "last worn" line — the fastest read on the whole panel. */
export function WearStatus({ c }: { c: CaseCompliance }) {
  if (!c.hasPatientApp) {
    return (
      <p className="text-sm text-muted">
        No patient app on this case yet - there is nothing to measure until a login is issued.
      </p>
    );
  }
  if (c.wearingNow) {
    return (
      <p className="inline-flex items-center gap-1.5 text-sm font-medium text-status-progress-fg">
        <IconCheck size={16} />
        Aligners in right now
      </p>
    );
  }
  return (
    <p className="inline-flex items-center gap-1.5 text-sm text-muted">
      <IconClock size={16} />
      {c.lastWornDay ? `Last recorded wear ${formatDate(c.lastWornDay)}` : 'The timer has never been started'}
    </p>
  );
}

export function LowWearNotice({ c, children }: { c: CaseCompliance; children?: ReactNode }) {
  if (!c.lowWear) return null;
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-status-warn-fg/30 bg-status-warn-bg/60 px-3 py-2.5">
      <span className="mt-0.5 shrink-0 text-status-warn-fg">
        <IconAlert size={18} />
      </span>
      <div className="min-w-0 text-sm text-status-warn-fg">
        <b>Low wear.</b> {c.daysBelowTarget7} of the last 7 days came in under the {c.targetHours}-hour
        goal.
        {children}
      </div>
    </div>
  );
}

// --- photos -------------------------------------------------------------------------------------

/** Photos grouped by the tray they were taken on, newest tray first. */
export function PhotoSeries({
  photos,
  urlFor,
  empty = 'No progress photos yet.',
}: {
  photos: ProgressPhoto[];
  urlFor: (p: ProgressPhoto) => string;
  empty?: string;
}) {
  const [open, setOpen] = useState<ProgressPhoto | null>(null);
  if (!photos.length) return <p className="text-sm text-muted">{empty}</p>;

  // Group by tray. `null` (photos taken before a tray plan existed) collects at the end.
  const groups = new Map<number | null, ProgressPhoto[]>();
  for (const p of photos) {
    const list = groups.get(p.trayNo) ?? [];
    list.push(p);
    groups.set(p.trayNo, list);
  }
  const ordered = [...groups.entries()].sort((a, b) => (b[0] ?? -1) - (a[0] ?? -1));

  return (
    <div className="space-y-4">
      {ordered.map(([tray, list]) => (
        <div key={String(tray)}>
          <div className="mb-1.5 flex items-baseline gap-2">
            <span className="text-[13px] font-semibold text-ink">{tray ? `Tray ${tray}` : 'Before the tray plan'}</span>
            <span className="text-xs text-muted">{formatDate(list[0]!.takenAt)}</span>
          </div>
          <ul className="no-scrollbar snap-strip flex gap-2 overflow-x-auto">
            {list.map((p) => (
              <li key={p.id} className="shrink-0">
                <button
                  type="button"
                  onClick={() => setOpen(p)}
                  className="tap block overflow-hidden rounded-md border border-line bg-surface-2"
                  aria-label={`${PHOTO_POSE_LABELS[p.pose]}, ${formatDate(p.takenAt)}`}
                >
                  <img
                    src={urlFor(p)}
                    alt={`${PHOTO_POSE_LABELS[p.pose]} taken ${formatDate(p.takenAt)}`}
                    loading="lazy"
                    className="h-28 w-28 object-cover sm:h-32 sm:w-32"
                  />
                  <span className="block px-2 py-1 text-[11px] font-medium text-muted">
                    {PHOTO_POSE_LABELS[p.pose]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <PhotoLightbox photo={open} url={open ? urlFor(open) : ''} onClose={() => setOpen(null)} />
    </div>
  );
}

function PhotoLightbox({ photo, url, onClose }: { photo: ProgressPhoto | null; url: string; onClose: () => void }) {
  useScrollLock(!!photo);
  useEffect(() => {
    if (!photo) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photo, onClose]);
  if (!photo) return null;
  return (
    <div
      className="fixed inset-0 z-modal flex flex-col bg-navy/90 animate-fade"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Progress photo"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 pt-safe text-white">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">
            {PHOTO_POSE_LABELS[photo.pose]}
            {photo.trayNo ? ` · tray ${photo.trayNo}` : ''}
          </div>
          <div className="text-xs text-white/70">{formatDate(photo.takenAt)}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="tap grid h-10 w-10 place-items-center rounded-full bg-white/10">
          <IconClose />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-4 pb-safe">
        <img src={url} alt={`${PHOTO_POSE_LABELS[photo.pose]}, full size`} className="max-h-full max-w-full rounded-lg object-contain" />
      </div>
      {photo.note && <p className="px-4 pb-4 text-center text-sm text-white/80">{photo.note}</p>}
    </div>
  );
}
